/**
 * Analytics Service
 * Tracks user interactions and page views
 */

const ANALYTICS_KEY = 'magicstay_analytics';
const SESSION_KEY = 'magicstay_session';

// Generate unique session ID
const generateSessionId = () => {
  return 'sess_' + Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Get or create session
const getSession = () => {
  let session = sessionStorage.getItem(SESSION_KEY);
  if (!session) {
    session = generateSessionId();
    sessionStorage.setItem(SESSION_KEY, session);
  }
  return session;
};

// Get stored analytics data
const getAnalyticsData = () => {
  try {
    return JSON.parse(localStorage.getItem(ANALYTICS_KEY) || '{}');
  } catch {
    return {};
  }
};

// Save analytics data
const saveAnalyticsData = (data) => {
  try {
    localStorage.setItem(ANALYTICS_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Could not save analytics data:', e);
  }
};

// Analytics service
export const analytics = {
  // Track page view
  pageView: (pageName, properties = {}) => {
    const sessionId = getSession();
    const data = getAnalyticsData();
    
    if (!data.pageViews) data.pageViews = [];
    
    data.pageViews.push({
      page: pageName,
      url: window.location.pathname,
      timestamp: new Date().toISOString(),
      sessionId,
      referrer: document.referrer,
      ...properties
    });

    // Keep only last 100 page views
    if (data.pageViews.length > 100) {
      data.pageViews = data.pageViews.slice(-100);
    }

    saveAnalyticsData(data);

    // Also send to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Page View:', pageName, properties);
    }
  },

  // Track custom event
  event: (eventName, properties = {}) => {
    const sessionId = getSession();
    const data = getAnalyticsData();
    
    if (!data.events) data.events = [];
    
    data.events.push({
      event: eventName,
      timestamp: new Date().toISOString(),
      sessionId,
      url: window.location.pathname,
      ...properties
    });

    // Keep only last 200 events
    if (data.events.length > 200) {
      data.events = data.events.slice(-200);
    }

    saveAnalyticsData(data);

    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Event:', eventName, properties);
    }
  },

  // Track conversion
  conversion: (conversionType, value = 0, properties = {}) => {
    const sessionId = getSession();
    const data = getAnalyticsData();
    
    if (!data.conversions) data.conversions = [];
    
    data.conversions.push({
      type: conversionType,
      value,
      timestamp: new Date().toISOString(),
      sessionId,
      url: window.location.pathname,
      ...properties
    });

    saveAnalyticsData(data);

    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Conversion:', conversionType, value, properties);
    }
  },

  // Track property view
  propertyView: (propertyId, propertyData = {}) => {
    analytics.event('property_view', {
      propertyId,
      ...propertyData
    });

    // Also save to recent views
    try {
      const recentViews = JSON.parse(localStorage.getItem('recent_views') || '[]');
      const existing = recentViews.findIndex(v => v.id === propertyId);
      
      if (existing !== -1) {
        recentViews.splice(existing, 1);
      }
      
      recentViews.unshift({
        id: propertyId,
        ...propertyData,
        viewedAt: new Date().toISOString()
      });

      // Keep only last 20 views
      localStorage.setItem('recent_views', JSON.stringify(recentViews.slice(0, 20)));
    } catch (e) {
      console.warn('Could not save recent view:', e);
    }
  },

  // Track search
  search: (query, filters = {}, resultsCount = 0) => {
    analytics.event('search', {
      query,
      filters,
      resultsCount
    });
  },

  // Track contact initiated
  contactInitiated: (propertyId, contactType, propertyData = {}) => {
    analytics.conversion('contact_initiated', 0, {
      propertyId,
      contactType,
      ...propertyData
    });
  },

  // Track favorite action
  favorite: (propertyId, action, propertyData = {}) => {
    analytics.event('favorite_' + action, {
      propertyId,
      ...propertyData
    });
  },

  // Track user signup
  signup: (method = 'email') => {
    analytics.conversion('signup', 0, { method });
  },

  // Track user login
  login: (method = 'email') => {
    analytics.event('login', { method });
  },

  // Get analytics summary
  getSummary: () => {
    const data = getAnalyticsData();
    
    return {
      totalPageViews: data.pageViews?.length || 0,
      totalEvents: data.events?.length || 0,
      totalConversions: data.conversions?.length || 0,
      uniquePages: [...new Set(data.pageViews?.map(p => p.page) || [])].length,
      topPages: getTopItems(data.pageViews, 'page', 5),
      topEvents: getTopItems(data.events, 'event', 5),
      recentActivity: [...(data.pageViews || []), ...(data.events || [])]
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 10)
    };
  },

  // Clear analytics data
  clear: () => {
    localStorage.removeItem(ANALYTICS_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  }
};

// Helper function to get top items by frequency
const getTopItems = (items, key, limit) => {
  if (!items || items.length === 0) return [];
  
  const counts = items.reduce((acc, item) => {
    acc[item[key]] = (acc[item[key]] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([name, count]) => ({ name, count }));
};

export default analytics;
